/**
 * Career Open Roles
 *
 * Wires List.js to the snippet rendered by template-career-open-roles.php.
 * Provides:
 *   - free-text search across job title (List.js's built-in search input)
 *   - two custom multi-select dropdowns (Department, Location) with pill chips
 *     and per-dropdown Clear buttons
 *   - one Clear-all button that resets all filters and the search box
 *   - URL state persistence via history.replaceState using repeated query
 *     params so option values containing commas
 *     (e.g. "Private Equity, Merchant, & Venture Banking", "New York, NY")
 *     don't break round-tripping
 *   - empty-state message
 *
 * Filter logic:
 *   AND across dimensions, OR within each multi-select dimension.
 *   Search runs over the job title only (the visible text inside
 *   `.careers-filterable--job-title`). Dept/loc filters compare against the
 *   item's `data-department` / `data-location` attributes (List.js exposes
 *   those via `valueNames: { data: [...] }`).
 *
 * Empty-location items are intentionally hidden whenever ANY Location filter
 * is active (per spec).
 */
(function () {
    'use strict';

    if (typeof window === 'undefined') return;

    function ready(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    }

    var PILL_X_SVG = '<span class="ts-multiselect--pill-x" aria-hidden="true">' +
        '<svg width="10" height="10" viewBox="0 0 10 10">' +
        '<path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg></span>';

    // Shared coordination across all multi-select instances so we only need
    // one set of document-level listeners regardless of how many filters are
    // rendered. `openMultiselect` is the currently-open instance (or null).
    var openMultiselect = null;
    var multiselectDocListenersAttached = false;

    function ensureMultiselectDocListeners() {
        if (multiselectDocListenersAttached) return;
        multiselectDocListenersAttached = true;
        document.addEventListener('click', function (e) {
            if (openMultiselect && !openMultiselect.root.contains(e.target)) {
                openMultiselect.close();
            }
        });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && openMultiselect) {
                // Cache before close() nulls the registry pointer so we can
                // still restore focus to the right toggle.
                var ms = openMultiselect;
                ms.close();
                ms.toggle.focus();
            }
        });
    }

    /**
     * initMultiselect
     *
     * Hooks up open/close, checkbox toggles, pill chips, and the per-dropdown
     * Clear button. Dispatches a `multiselect:change` CustomEvent on the root
     * whenever the selected values change so the outer controller can rerun
     * List.js's filter.
     */
    function initMultiselect(root) {
        var toggle      = root.querySelector('.ts-multiselect--toggle');
        var panel       = root.querySelector('.ts-multiselect--panel');
        // The toggle's "placeholder" span (e.g. "All departments") is
        // shown when no values are selected and hidden once pill chips
        // appear, mirroring the old `.ts-multiselect--label` behavior.
        var placeholder = root.querySelector('.ts-multiselect--placeholder');
        var pills       = root.querySelector('.ts-multiselect--pills');
        var hidden      = root.querySelector('input[type="hidden"]');
        // The per-filter Clear button now lives in the field header
        // above the toggle (not inside the dropdown panel) and is
        // hidden until at least one option is selected.
        var clearer     = root.querySelector('.ts-multiselect--clear');
        var checks      = root.querySelectorAll('input[type="checkbox"]');

        if (!toggle || !panel) return;

        // Forward declaration so open()/close() can reference the registry
        // entry that uniquely identifies this instance.
        var instance;

        function isOpen() { return toggle.getAttribute('aria-expanded') === 'true'; }
        function open() {
            // Coordinate via the shared pointer so only one panel is open
            // at a time -- replaces the old `ts-multiselect:open` event.
            if (openMultiselect && openMultiselect !== instance) {
                openMultiselect.close();
            }
            panel.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
            openMultiselect = instance;
        }
        function close() {
            panel.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
            if (openMultiselect === instance) openMultiselect = null;
        }

        instance = { root: root, toggle: toggle, close: close };
        ensureMultiselectDocListeners();

        function uncheck(value) {
            Array.prototype.forEach.call(checks, function (c) {
                if (c.value === value) c.checked = false;
            });
        }

        function renderPills(values) {
            pills.textContent = '';
            values.forEach(function (v) {
                var pill = document.createElement('span');
                pill.className = 'ts-multiselect--pill';
                pill.dataset.value = v;
                pill.title = 'Remove ' + v;
                var text = document.createElement('span');
                text.className = 'ts-multiselect--pill-text';
                text.textContent = v;
                pill.appendChild(text);
                pill.insertAdjacentHTML('beforeend', PILL_X_SVG);
                pills.appendChild(pill);
            });
        }

        function sync() {
            var values = Array.prototype.filter.call(checks, function (c) { return c.checked; })
                                               .map(function (c) { return c.value; });
            if (hidden) hidden.value = values.join(',');
            if (values.length > 0) {
                if (placeholder) placeholder.hidden = true;
                pills.hidden = false;
                renderPills(values);
                if (clearer) clearer.hidden = false;
            } else {
                if (placeholder) placeholder.hidden = false;
                pills.hidden = true;
                pills.textContent = '';
                if (clearer) clearer.hidden = true;
            }
            root.dispatchEvent(new CustomEvent('multiselect:change', {
                detail: { name: root.dataset.name, values: values }
            }));
        }

        toggle.addEventListener('click', function (e) {
            e.stopPropagation();
            // Clicking a pill removes that single value rather than toggling
            // the dropdown. Pills are spans (not nested buttons) so they
            // don't violate HTML; we route the click through the toggle.
            var pill = e.target.closest && e.target.closest('.ts-multiselect--pill');
            if (pill) {
                uncheck(pill.dataset.value);
                sync();
                return;
            }
            isOpen() ? close() : open();
        });
        panel.addEventListener('click', function (e) { e.stopPropagation(); });
        Array.prototype.forEach.call(checks, function (c) { c.addEventListener('change', sync); });
        if (clearer) {
            clearer.addEventListener('click', function (e) {
                e.stopPropagation();
                Array.prototype.forEach.call(checks, function (c) { c.checked = false; });
                sync();
            });
        }
        sync();
    }

    ready(function () {
        var root = document.getElementById('careers-filterable-root');
        if (!root) return;
        if (typeof List !== 'function') {
            // If List.js failed to load just fall back to a plain page; the
            // multi-selects still render, but no filtering happens.
            return;
        }

        var listEl    = root.querySelector('.careers-filterable--list');
        var searchEl  = root.querySelector('.careers-filterable--search-input');
        var clearAll  = root.querySelector('.careers-filterable--clear-all');
        var emptyEl   = root.querySelector('[data-careers-empty]');

        if (!listEl) return;

        // ---- URL <-> filter state ------------------------------------------
        function readUrlState() {
            var params = new URLSearchParams(window.location.search);
            return {
                q: params.get('q') || '',
                department: params.getAll('department'),
                location:   params.getAll('location')
            };
        }

        function writeUrlState() {
            var params = new URLSearchParams();
            selected.department.forEach(function (v) { params.append('department', v); });
            selected.location.forEach(function (v)   { params.append('location',   v); });
            if (query) params.set('q', query);
            var qs = params.toString();
            var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
            var current = window.location.pathname + window.location.search + window.location.hash;
            if (newUrl !== current) {
                window.history.replaceState(null, '', newUrl);
            }
        }

        var initialState = readUrlState();
        var selected = {
            department: initialState.department.slice(),
            location:   initialState.location.slice()
        };
        var query = (initialState.q || '').toLowerCase();

        // ---- Pre-check checkboxes from URL state, then init multi-selects --
        var multiSelects = root.querySelectorAll('.careers-filterable--multiselect');
        Array.prototype.forEach.call(multiSelects, function (ms) {
            var name = ms.dataset.name;
            var values = initialState[name] || [];
            if (values.length) {
                ms.querySelectorAll('input[type="checkbox"]').forEach(function (c) {
                    if (values.indexOf(c.value) !== -1) c.checked = true;
                });
            }
        });
        Array.prototype.forEach.call(multiSelects, initMultiselect);

        if (searchEl && initialState.q) {
            searchEl.value = initialState.q;
        }

        // ---- List.js setup -------------------------------------------------
        // valueNames maps DOM elements (by class) and data-attributes onto
        // List.js item.values(). We rely on `data-department`/`data-location`
        // for filter comparisons rather than the visible text so our matches
        // are exact, even when the visible meta string is decorated.
        var listJs;
        try {
            listJs = new List(root, {
                listClass:    'careers-filterable--list',
                searchClass:  'careers-filterable--search-input',
                valueNames: [
                    'careers-filterable--job-title',
                    { data: ['department', 'location'] }
                ]
            });
        } catch (e) {
            return;
        }

        // ---- Filter callback ------------------------------------------------
        function applyFilter() {
            var deptActive = selected.department.length > 0;
            var locActive  = selected.location.length > 0;

            if (!deptActive && !locActive) {
                listJs.filter();
            } else {
                listJs.filter(function (item) {
                    var v = item.values();
                    if (deptActive && selected.department.indexOf(v.department || '') === -1) {
                        return false;
                    }
                    if (locActive && selected.location.indexOf(v.location || '') === -1) {
                        return false;
                    }
                    return true;
                });
            }
        }

        // ---- Update empty state, clear-all visibility ----------------------
        function updateChrome() {
            if (emptyEl) {
                emptyEl.hidden = listJs.matchingItems.length > 0;
            }

            if (clearAll) {
                var hasFilters = !!(selected.department.length || selected.location.length || query);
                clearAll.hidden = !hasFilters;
            }
        }

        listJs.on('updated', updateChrome);

        // ---- Wire multiselect change events --------------------------------
        Array.prototype.forEach.call(multiSelects, function (ms) {
            ms.addEventListener('multiselect:change', function (e) {
                selected[e.detail.name] = e.detail.values;
                applyFilter();
                writeUrlState();
            });
        });

        // ---- Search input
        // We drive List.js search ourselves rather than relying on its
        // auto-bound listener so we can:
        //   1. Restrict matching to the visible job title (otherwise the
        //      `data: ['department', 'location']` valueNames would also
        //      participate in fuzzy matching, producing misleading hits).
        //   2. Mirror the typed query into our URL state on every keystroke.
        if (searchEl) {
            searchEl.addEventListener('input', function () {
                query = (searchEl.value || '').trim().toLowerCase();
                listJs.search(query, ['careers-filterable--job-title']);
                writeUrlState();
            });
            if (initialState.q) {
                listJs.search(initialState.q, ['careers-filterable--job-title']);
            }
        }

        // ---- Clear-all -----------------------------------------------------
        if (clearAll) {
            clearAll.addEventListener('click', function () {
                // Trigger each multi-select's existing Clear button so the
                // pill chips, hidden inputs, and `multiselect:change` events
                // all stay in sync without us reaching into their internals.
                root.querySelectorAll('.ts-multiselect--clear').forEach(function (btn) {
                    btn.click();
                });
                if (searchEl) {
                    searchEl.value = '';
                    listJs.search('', ['careers-filterable--job-title']);
                }
                query = '';
                applyFilter();
                writeUrlState();
                if (searchEl) searchEl.focus();
            });
        }

        // ---- Initial pass --------------------------------------------------
        applyFilter();
        // Force a chrome update in case applyFilter() didn't trigger
        // `updated` (e.g. when no filters are active and the list was already
        // showing everything).
        updateChrome();
    });
})();
